import type { CommunicationTemplate } from '../../data/types';

export interface TemplateDefinition {
  key: CommunicationTemplate;
  name: string;
  trigger: string;
  subject: string;
  body: string;
}

export const TEMPLATES: TemplateDefinition[] = [
  {
    key: 'INVESTOR_REPORT',
    name: 'Quarterly investor report',
    trigger: 'Sent when a quarterly report run is approved',
    subject: '{{title}}',
    body: 'Dear {{firstName}},\n\n{{message}}\n\nYour quarterly report and your personal statement as at {{quarterEnd}} are attached and in the Documents section of the app.',
  },
  {
    key: 'CALL_NOTICE',
    name: 'Capital call notice',
    trigger: 'Sent when a capital call is approved',
    subject: 'Capital call notice: {{fund}} Call {{number}}',
    body: 'Dear {{firstName}},\n\nThe trustee has called {{percent}} of your commitment to {{fund}}. Please pay {{amount}} by {{dueDate}} using BPAY biller {{biller}} and reference {{reference}}.\n\nYour notice is in the Documents section of the app.',
  },
  {
    key: 'CALL_REMINDER',
    name: 'Capital call reminder',
    trigger: '7, 3 and 1 days before the due date, to holders yet to pay',
    subject: 'Reminder: {{fund}} Call {{number}} is due {{dueDate}}',
    body: 'Dear {{firstName}},\n\nA reminder that {{amount}} is due on {{dueDate}}. If you have already paid, thank you; it can take a day to appear.',
  },
  {
    key: 'DEFAULT_NOTICE',
    name: 'Notice of default',
    trigger: 'Issued manually after the due date',
    subject: 'Notice of default: {{fund}} Call {{number}}',
    body: 'Dear {{firstName}},\n\n{{amount}} due on {{dueDate}} remains unpaid. Default interest applies from the due date. If unpaid within 14 days, the trustee may exercise its remedies under the trust deed.',
  },
  {
    key: 'DISTRIBUTION_ADVICE',
    name: 'Distribution paid',
    trigger: 'Sent when a distribution payment is released',
    subject: 'Your {{fund}} distribution has been paid',
    body: 'Dear {{firstName}},\n\nYour distribution of {{amount}} for the period ending {{periodEnd}} has been paid. Your advice is in the app.',
  },
  {
    key: 'CAPITAL_RETURN_NOTICE',
    name: 'Capital returned',
    trigger: 'Sent when a partial capital return is paid',
    subject: '{{fund}}: part of your capital has been returned',
    body: 'Dear {{firstName}},\n\nFollowing a repayment, {{amount}} of your capital has been returned. Your remaining units continue to earn income.',
  },
  {
    key: 'WIND_UP_NOTICE',
    name: 'Wind-up and final payment',
    trigger: 'Sent when a trust is wound up',
    subject: '{{fund}} has repaid: your capital has been returned',
    body: 'Dear {{firstName}},\n\nThe loan has been repaid and the trust has been wound up. We have paid {{amount}} (capital plus final income). Your exit statement is in the app.',
  },
  {
    key: 'TAX_STATEMENT_READY',
    name: 'Tax statement ready',
    trigger: 'Sent when AMMA statements are published',
    subject: 'Your {{year}} tax statement is ready',
    body: 'Dear {{firstName}},\n\nYour {{year}} AMMA tax statement is ready in the Tax centre. You can also give your accountant read-only access.',
  },
  {
    key: 'APPLICATION_REMINDER',
    name: 'Finish your application',
    trigger: 'Sent 3 days after an application stalls',
    subject: 'Finish your {{fund}} application',
    body: 'Dear {{firstName}},\n\nYour application is saved. It takes a few minutes to finish in the app.',
  },
  {
    key: 'WHOLESALE_RENEWAL',
    name: 'Wholesale certificate renewal',
    trigger: '60 days before a certificate expires',
    subject: 'Your wholesale investor certificate is due for renewal',
    body: 'Dear {{firstName}},\n\nYour accountant’s certificate expires on {{expiry}}. Upload a new one in the app to keep investing in new offers.',
  },
  {
    key: 'REDEMPTION_CONFIRMATION',
    name: 'Redemption paid',
    trigger: 'Sent when a redemption run is paid',
    subject: 'Your {{fund}} redemption has been paid',
    body: 'Dear {{firstName}},\n\nYour redemption at the {{window}} window has been paid. Your confirmation is in the app.',
  },
  {
    key: 'GENERAL_UPDATE',
    name: 'General update',
    trigger: 'Sent manually',
    subject: '{{subject}}',
    body: 'Dear {{firstName}},\n\n{{message}}',
  },
];

export function templateFor(key: CommunicationTemplate): TemplateDefinition {
  return TEMPLATES.find((template) => template.key === key) ?? TEMPLATES[TEMPLATES.length - 1];
}
